import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/founder", label: "Founder" },
  { to: "/audit", label: "Risk Audit", highlight: true },
  { to: "https://blog.unitedtechlab.com", label: "Blog", external: true },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-obsidian/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative inline-flex items-center justify-center h-9 w-9 border border-neon-green/50 bg-obsidian">
            <span className="absolute inset-1 bg-neon-green/10 animate-pulse-glow rounded-sm" />
            <span className="relative font-display font-black text-neon-green text-lg leading-none">
              U
            </span>
          </span>
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold tracking-tight text-white text-base">
              United<span className="text-neon-green">Techlab</span>
            </span>
            <span className="chip text-[10px] mt-[3px]">&gt; verification.first</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-2 text-sm font-mono text-white/70 hover:text-neon-green transition-colors"
              >
                {l.label} ↗
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-mono transition-colors inline-flex items-center gap-1.5 ${
                    isActive
                      ? "text-neon-green"
                      : l.highlight
                      ? "text-neon-cyan hover:text-neon-green"
                      : "text-white/70 hover:text-white"
                  }`
                }
              >
                {l.highlight && <Zap size={12} />}
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            data-testid="navbar-cta-request-demo"
            className="ml-4 inline-flex items-center gap-2 bg-neon-green text-obsidian px-5 py-2.5 text-sm font-mono font-semibold uppercase tracking-wider hover:bg-white hover:shadow-[0_0_24px_rgba(0,255,65,0.6)] transition-all"
          >
            Request Demo <ChevronRight size={16} />
          </Link>
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-xl overflow-hidden"
          >
            <nav className="flex flex-col px-5 py-4 gap-1">
              {links.map((l) =>
                l.external ? (
                  <a
                    key={l.to}
                    href={l.to}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="py-3 font-mono text-white/80 border-b border-white/5"
                  >
                    {l.label} ↗
                  </a>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`py-3 font-mono border-b border-white/5 inline-flex items-center gap-2 ${
                      l.highlight ? "text-neon-cyan" : "text-white/80"
                    }`}
                  >
                    {l.highlight && <Zap size={12} />}
                    {l.label}
                  </NavLink>
                )
              )}
              <Link
                to="/contact"
                data-testid="mobile-navbar-cta"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-neon-green text-obsidian px-5 py-3 text-sm font-mono font-semibold uppercase tracking-wider"
              >
                Request Demo <ChevronRight size={16} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
